import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useCookies } from 'react-cookie';

export default function EditIngredient() {
    const [cookies] = useCookies(["access_token"]);
    const { state } = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [ingredient, setIngredient] = useState(state?.ingredient || {});
    const [showSpecialOptions, setShowSpecialOptions] = useState(false);
    const [error, setError] = useState('');
    const [imagePreview, setImagePreview] = useState(ingredient.imageUrl || null);

    useEffect(() => {
        // Initialize showSpecialOptions based on whether there's an active special
        setShowSpecialOptions(!!ingredient.special?.active);
        // Initialize image preview if ingredient has an image
        if (ingredient.imageUrl) {
            setImagePreview(ingredient.imageUrl);
        }
    }, [ingredient.special?.active, ingredient.imageUrl]);

    const handleChange = (field, value) => {
        if (field.startsWith('special.')) {
            const [parent, child] = field.split('.');
            setIngredient(prev => {
                const newSpecial = {
                    ...prev.special,
                    [child]: value,
                    active: true
                };

                // Calculate savings for MULTI_BUY
                if (child === 'specialPrice' || child === 'buyQuantity') {
                    if (newSpecial.type === 'MULTI_BUY' && 
                        newSpecial.buyQuantity && 
                        newSpecial.specialPrice) {
                        const originalCost = newSpecial.buyQuantity * prev.costPerUnit;
                        const savings = originalCost - newSpecial.specialPrice;
                        newSpecial.savingsAmount = savings;
                        newSpecial.savingsPercentage = ((savings / originalCost) * 100).toFixed(1);
                    }
                }

                return {
                    ...prev,
                    special: newSpecial
                };
            });
        } else if (field.startsWith('unitEquivalent.')) {
            const [parent, child] = field.split('.');
            setIngredient(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setIngredient(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Create a preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            setIngredient(prev => ({
                ...prev,
                image: file
            }));
        }
    };

    const handleSpecialToggle = (e) => {
        const isChecked = e.target.checked;
        setShowSpecialOptions(isChecked);
        
        if (!isChecked) {
            // Reset special fields when unchecking
            setIngredient(prev => ({
                ...prev,
                special: {
                    type: '',
                    buyQuantity: '',
                    getQuantity: '',
                    specialPrice: '',
                    savingsAmount: '',
                    savingsPercentage: '',
                    active: false
                }
            }));
        } else if (!ingredient.special) {
            // Initialize special object if it doesn't exist
            setIngredient(prev => ({
                ...prev,
                special: {
                    type: '',
                    buyQuantity: '',
                    getQuantity: '',
                    specialPrice: '',
                    savingsAmount: '',
                    savingsPercentage: '',
                    active: true
                }
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Create FormData for multipart/form-data
            const formData = new FormData();
            formData.append('name', ingredient.name);
            formData.append('costPerUnit', ingredient.costPerUnit);
            formData.append('baseUnit', ingredient.baseUnit);
            formData.append('brand', ingredient.brand);

            if (ingredient.image) {
                formData.append('image', ingredient.image);
            }

            if (ingredient.unitEquivalent?.amount) {
                formData.append('unitEquivalent[amount]', ingredient.unitEquivalent.amount);
                formData.append('unitEquivalent[unit]', ingredient.baseUnit);
            }

            if (ingredient.special?.active) {
                formData.append('special[type]', ingredient.special.type);
                formData.append('special[active]', true);
                
                switch (ingredient.special.type) {
                    case 'MULTI_BUY':
                        formData.append('special[buyQuantity]', ingredient.special.buyQuantity);
                        formData.append('special[specialPrice]', ingredient.special.specialPrice);
                        break;
                    case 'PERCENTAGE_OFF':
                        formData.append('special[savingsPercentage]', ingredient.special.savingsPercentage);
                        break;
                    case 'FIXED_PRICE':
                        formData.append('special[specialPrice]', ingredient.special.specialPrice);
                        break;
                    case 'BUY_X_GET_Y':
                        formData.append('special[buyQuantity]', ingredient.special.buyQuantity);
                        formData.append('special[getQuantity]', ingredient.special.getQuantity);
                        break;
                }
            }

            await axios.put(`${API_BASE_URL}/ingredients/${id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${cookies.access_token}`
                }
            });
            navigate('/ingredient-management');
        } catch (error) {
            setError(error.response?.data?.message || 'Error updating ingredient');
        }
    };

    return (
        <div className="edit-ingredient">
            <h1>Edit Ingredient</h1>
            {error && <div className="error">{error}</div>}
            <form onSubmit={handleSubmit}>
                {/* Image upload section */}
                <div className="image-section">
                    <label>Ingredient Image:</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ marginBottom: '10px' }}
                    />
                    {imagePreview && (
                        <div className="image-preview">
                            <img 
                                src={imagePreview} 
                                alt="Ingredient preview" 
                                style={{
                                    maxWidth: '100px',
                                    maxHeight: '100px',
                                    objectFit: 'cover',
                                    marginBottom: '10px',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                    )}
                </div>

                <div>
                    <label>Name:</label>
                    <input
                        type="text"
                        value={ingredient.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                    />
                </div>
                <div>
                    <label>Cost Per Unit:</label>
                    <input
                        type="number"
                        step="0.01"
                        value={ingredient.costPerUnit || ''}
                        onChange={(e) => handleChange('costPerUnit', e.target.value)}
                    />
                </div>
                <div>
                    <label>Base Unit:</label>
                    <select
                        value={ingredient.baseUnit || ''}
                        onChange={(e) => handleChange('baseUnit', e.target.value)}
                    >
                        <option value="">Select base unit</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="l">l</option>
                        <option value="ml">ml</option>
                        <option value="unit">unit</option>
                        <option value="R/kg">R/kg</option>
                        <option value="whole">whole</option>
                    </select>
                </div>
                {ingredient.baseUnit && ingredient.baseUnit !== 'R/kg' && (
                    <div>
                        <label>Unit Equivalent Amount:</label>
                        <input
                            type="number"
                            step="0.01"
                            value={ingredient.unitEquivalent?.amount || ''}
                            onChange={(e) => handleChange('unitEquivalent.amount', e.target.value)}
                        />
                    </div>
                )}
                <div>
                    <label>Brand:</label>
                    <input
                        type="text"
                        value={ingredient.brand || ''}
                        onChange={(e) => handleChange('brand', e.target.value)}
                    />
                </div>

                {/* Special Offer Section */}
                <div className="special-section">
                    <label>
                        <input
                            type="checkbox"
                            checked={showSpecialOptions}
                            onChange={handleSpecialToggle}
                        />
                        Has Special Offer
                    </label>

                    {showSpecialOptions && (
                        <div className="special-options">
                            <select
                                value={ingredient.special?.type || ''}
                                onChange={(e) => handleChange('special.type', e.target.value)}
                            >
                                <option value="">Select Special Type</option>
                                <option value="MULTI_BUY">Buy X for Price Y</option>
                                <option value="PERCENTAGE_OFF">Percentage Off</option>
                                <option value="FIXED_PRICE">Fixed Price Special</option>
                                <option value="BUY_X_GET_Y">Buy X Get Y</option>
                            </select>

                            {ingredient.special?.type === 'MULTI_BUY' && (
                                <>
                                    <input
                                        type="number"
                                        placeholder="Buy Quantity"
                                        value={ingredient.special?.buyQuantity || ''}
                                        onChange={(e) => handleChange('special.buyQuantity', e.target.value)}
                                    />
                                    <select
                                        value={ingredient.special?.discountType || 'FIXED'}
                                        onChange={(e) => handleChange('special.discountType', e.target.value)}
                                    >
                                        <option value="FIXED">Fixed Price</option>
                                        <option value="PERCENTAGE">Percentage Off</option>
                                    </select>
                                    {ingredient.special?.discountType === 'FIXED' ? (
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Special Price"
                                            value={ingredient.special?.specialPrice || ''}
                                            onChange={(e) => handleChange('special.specialPrice', e.target.value)}
                                        />
                                    ) : (
                                        <input
                                            type="number"
                                            placeholder="Discount Percentage"
                                            value={ingredient.special?.discountPercent || ''}
                                            onChange={(e) => handleChange('special.discountPercent', e.target.value)}
                                        />
                                    )}
                                </>
                            )}

                            {ingredient.special?.type === 'PERCENTAGE_OFF' && (
                                <input
                                    type="number"
                                    placeholder="Savings Percentage"
                                    value={ingredient.special?.savingsPercentage || ''}
                                    onChange={(e) => handleChange('special.savingsPercentage', e.target.value)}
                                />
                            )}

                            {ingredient.special?.type === 'FIXED_PRICE' && (
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Special Price"
                                    value={ingredient.special?.specialPrice || ''}
                                    onChange={(e) => handleChange('special.specialPrice', e.target.value)}
                                />
                            )}

                            {ingredient.special?.type === 'BUY_X_GET_Y' && (
                                <>
                                    <input
                                        type="number"
                                        placeholder="Buy Quantity"
                                        value={ingredient.special?.buyQuantity || ''}
                                        onChange={(e) => handleChange('special.buyQuantity', e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Get Quantity"
                                        value={ingredient.special?.getQuantity || ''}
                                        onChange={(e) => handleChange('special.getQuantity', e.target.value)}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="button-container">
                    <button type="submit">Update Ingredient</button>
                    <button type="button" onClick={() => navigate('/ingredient-management')}>
                        Cancel
                    </button>
                </div>
            </form>

            <style>
            {`
                .edit-ingredient {
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 20px;
                }

                .error {
                    color: red;
                    margin-bottom: 10px;
                }

                form > div {
                    margin-bottom: 15px;
                }

                label {
                    display: block;
                    margin-bottom: 5px;
                }

                input, select {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                }

                .image-section {
                    margin-bottom: 20px;
                }

                .image-preview {
                    margin-top: 10px;
                    border: 1px solid #ddd;
                    padding: 5px;
                    border-radius: 4px;
                    display: inline-block;
                }

                .special-section {
                    border: 1px solid #ddd;
                    padding: 15px;
                    border-radius: 4px;
                    margin-bottom: 15px;
                }

                .special-options {
                    margin-top: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .button-container {
                    display: flex;
                    gap: 10px;
                }

                button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                }

                button[type="submit"] {
                    background-color: #28a745;
                    color: white;
                }

                button[type="button"] {
                    background-color: #6c757d;
                    color: white;
                }
            `}
            </style>
        </div>
    );
}
